import type { ApiCountry } from '@mfw/common/ui/dtos';

import { CountriesEnum } from '@mfw/common/ui/dtos';
import { z } from 'zod';

import type { MaybeBoolean, MaybeString } from '../types/maybe.types';

export const ApiAddressCountrySchema = z.object({
  countryCode: CountriesEnum,
  dialCode: z.string().or(z.literal('')),
  label: z.string().or(z.literal('')),
});

export type ApiAddressCountry = {
  countryCode: z.infer<typeof CountriesEnum>;
  dialCode: string;
  label: string;
};

export type ApiAddressCountryCode = ApiAddressCountry['countryCode'];

export type ApiAddress = {
  country: ApiAddressCountry;
  street?: string;
  city: string;
  zip: string;
};

/**
 * FE schemas and types
 */
export const UiAddressCountrySchema = z.object({
  dialCode: z.string().or(z.literal('')).or(z.undefined()),
  label: z.string().or(z.literal('')).or(z.undefined()),
  value: CountriesEnum,
});

export type UiAddressCountry = z.infer<typeof UiAddressCountrySchema>;

export const UiAddressSchema = z.object({
  city: z
    .string()
    .min(1, { message: 'VALIDATION__Required' })
    .max(255, { message: 'VALIDATION__Max255CharLimit' }),
  country: z.array(UiAddressCountrySchema),
  isAddressPublic: z.boolean().optional(),
  street: z
    .string()
    .max(255, { message: 'VALIDATION__Max255CharLimit' })
    .min(3, { message: 'VALIDATION__Required' })
    .or(z.literal(''))
    .transform((val) => (val === '' ? undefined : val))
    .or(z.undefined()),
  zip: z
    .string()
    .min(1, { message: 'VALIDATION__Required' })
    .max(255, { message: 'VALIDATION__Max255CharLimit' }),
});

/**
 * TODO Move this to be fetched from App config once we move it to BE
 */
export const DEFAULT_COUNTRY = {
  dialCode: '+381',
  label: 'Serbia',
  value: CountriesEnum.Enum.RS,
} satisfies UiAddressCountry;

export type UiAddressCountryCode = ApiAddressCountryCode;

export const UiAddressCreateSchema = UiAddressSchema;

export type UiAddress = z.infer<typeof UiAddressSchema>;

export const DEFAULT_COUNTRY_CODE = CountriesEnum.Enum.RS;

export type ApiAddressRecord = {
  isAddressPublic: MaybeBoolean;
  country: ApiCountry;
  street: MaybeString;
  state: MaybeString;
  city: MaybeString;
  zip: MaybeString;
  // F0 (public-page): backfilled in Task 1 so pickPrimary can pick "the one"
  // address without guessing an ORDER BY.
  isPrimary?: boolean | null;
  sortOrder?: number | null;
};

export type UiAddressRecord = {
  country: [{ value: ApiCountry }];
} & Omit<ApiAddressRecord, 'country'>;
